import React, { Component } from "react";
import { observer, inject } from "mobx-react";
import { FormGroup, Button, Intent } from "@blueprintjs/core";
import moment from 'moment-timezone';
import DateTimeInput from "components/DateTimeInput";
import FormDrawer from "components/FormDrawer";
import Toast from "helpers/toaster";
import { observable } from "mobx";
import ClientAutocomplete from "components/ClientAutocomplete";
import Lockr from "lockr";
import { FormSection, Choice, FieldError, MoreOptions } from "components/FormBits";

const STATUSES = [
  { value: "active", label: "Active" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "invoiced", label: "Invoiced" },
];

const PROJECT_TYPES = [
  { value: null, label: "Not set" },
  { value: "Q", label: "Quoted" },
  { value: "OP", label: "OP" },
];

const FIELD_IDS = {
  client: "add-project",
  name: "project-name",
  status: "project-status",
};

const focusField = (id) => {
  const element = document.getElementById(id);
  const target = element && element.matches("button, input, select, textarea") ? element : element && element.querySelector("button, input");
  if (target) {
    target.focus();
    target.scrollIntoView({ block: "center" });
  }
};

@inject("projectStore", "quoteStore", "store")
@observer
class AddProjectModal extends Component {
  @observable client_id = null;
  @observable client_name = null;
  // The `client` prop, shown as the autocomplete's selection until the user clears it.
  @observable preselectedClient = null;
  @observable errors = {};

  componentDidMount() {
    if (this.props.isOpen) {
      this.resetForm();
      this.preselectClient();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.resetForm();
      this.preselectClient();
    }
  }

  // The dialog stays mounted between opens, so start each one from a blank
  // form (fresh dates) with no customer left over from the last project.
  resetForm() {
    this.props.projectStore.clearNewProject();
    this.client_id = null;
    this.preselectedClient = null;
  }

  // Opened with a `client`: select it as if it was picked in the autocomplete.
  // A new project starts as Active unless a status is already chosen.
  preselectClient() {
    let { object } = this.props.projectStore.newProject;
    if (object && !object.status) {
      object.status = "active";
    }
    let { client } = this.props;
    if (!client) {
      return;
    }
    this.preselectedClient = client;
    this.onClientSelect(client);
  }

  onClientSelect(client) {
    if (client) {
      this.client_id = client.id;
      if (this.errors.client) this.errors = { ...this.errors, client: null };
    } else {
      this.client_id = null;
      this.preselectedClient = null;
    }
  }

  createProject() {
    let {
      props: {
        projectStore: {
          newProject: { object }
        }
      }
    } = this;

    let {
      props: {       
        store
      }
    } = this;
   
    if (this.props.client_id) {
      object.client_id = this.props.client_id;
    } else {
      object.client_id = this.client_id;
    }

    // A quote's client (left in the store after visiting a quote) must not
    // replace the client this dialog was opened for.
    if(store.projectDetail.client && !this.props.client){
      object.client_id = store.projectDetail.client.id;  
      object.status =  'active';    
    }
    

    const errors = {};
    if (!object.client_id) errors.client = "Pick the client this project is for.";
    if (!object.name) errors.name = "Give the project a name.";
    if (!object.status) errors.status = "Pick a status.";
    this.errors = errors;
    const first = Object.keys(FIELD_IDS).find((field) => errors[field]);
    if (first) {
      focusField(FIELD_IDS[first]);
      return;
    }

    this.props.projectStore
      .createProject()
      .then(this.onSuccessfullyCreateProject.bind(this))
      .catch(this.onFailCreateProject.bind(this));
  }

  onSuccessfullyCreateProject(response) {
    this.forgetEstimate();
    this.props.onSuccess(response);
  }

  // Approving an estimate opens this panel for the estimate's client
  // (store.projectDetail). Forget it once the panel is done, so a project
  // created later from anywhere else isn't silently given that client.
  forgetEstimate() {
    if (this.props.store) this.props.store.projectDetail = [];
  }

  onFailCreateProject(error) {
    const errors = error && error.response && error.response.data && error.response.data.errors;
    Toast.show({
      icon: "warning-sign",
      message: Array.isArray(errors) && errors.length ? errors.join(", ") : "Unable to create the project. Please try again.",
      intent: Intent.DANGER
    });
    console.log(error);
  }

  handleCloseProjectDailog() {
    this.errors = {};
    this.forgetEstimate();
    this.props.onClose();
  }

  onProjectStatus(selectedOptions) {
    this.projectStatusSelectedOptions = selectedOptions;
    let { object } = this.props.projectStore.newProject;
    if (selectedOptions.length > 0) {
      object.status = selectedOptions[0].value;
    }
  }

  setField(field, value) {
    this.props.projectStore.newProject.object[field] = value;
    if (this.errors[field]) {
      this.errors = { ...this.errors, [field]: null };
    }
  }

  handleStartDateChange(date) {
    let { object } = this.props.projectStore.newProject;
    object.start_date = moment(date)
  }

  handleEndDateChange(date) {
    let { object } = this.props.projectStore.newProject;
    object.end_date = moment(date)
  }

  render() {
    const object = this.props.projectStore.newProject.object || {};
    let { client_id } = this.props;
    let {
      props: { store }
    } = this;
    const errors = this.errors;

    // After visiting an estimate, a new project (unless opened for a client)
    // is created for that estimate's client, as Active: say so, instead of
    // offering a client picker and statuses that save would replace.
    const estimateClient = store.projectDetail && store.projectDetail.client && !this.props.client ? store.projectDetail.client : null;
    const status = estimateClient ? "active" : object.status;

    const currentUser = Lockr.get("currentUser");
    const isLasercontrolsTenant = currentUser && currentUser.attributes && ["lasercontrols", "field_nexus_demo"].includes(currentUser.attributes.tenant_name);
    const hasMore = Boolean(object.po_number || object.price || (isLasercontrolsTenant && object.project_type));

    const busy = !!this.props.projectStore.newProject.isLoading;

    return (
      <FormDrawer
        size="min(760px, 100vw)"
        title="New project"
        icon="projects"
        isOpen={this.props.isOpen}
        onClose={this.handleCloseProjectDailog.bind(this)}
        busy={busy}
        footer={
          <>
            <Button text="Cancel" onClick={this.handleCloseProjectDailog.bind(this)} disabled={busy} />
            <Button
              intent={Intent.PRIMARY}
              icon="add"
              text="Create project"
              loading={busy}
              onClick={this.createProject.bind(this)}
            />
          </>
        }
      >
        <FormSection title="Client and project" hint="Who it's for and what your team will call it.">
          {estimateClient ? (
            <FormGroup
              label="Client"
              labelFor="project-estimate-client"
              helperText="Taken from the estimate you last opened."
            >
              <input
                id="project-estimate-client"
                type="text"
                className="bp3-input bp3-fill"
                readOnly
                value={estimateClient.name || ""}
              />
            </FormGroup>
          ) : !client_id && (
            <FormGroup
              label="Client"
              labelFor={FIELD_IDS.client}
              labelInfo={<span className="fb-required" aria-hidden="true">*</span>}
              className={errors.client ? "has-error" : undefined}
            >
              <ClientAutocomplete
                value={this.preselectedClient}
                inputProps={{
                  id: FIELD_IDS.client,
                  placeholder: "Start typing a name, phone number or email",
                  "aria-required": true,
                  "aria-invalid": errors.client ? true : undefined,
                  "aria-describedby": errors.client ? `${FIELD_IDS.client}-error` : undefined
                }}
                onClientSelect={this.onClientSelect.bind(this)}
              />
              <FieldError id={`${FIELD_IDS.client}-error`}>{errors.client}</FieldError>
            </FormGroup>
          )}
          <FormGroup
            label="Project name"
            labelFor={FIELD_IDS.name}
            labelInfo={<span className="fb-required" aria-hidden="true">*</span>}
            className={errors.name ? "has-error" : undefined}
          >
            <input
              id={FIELD_IDS.name}
              type="text"
              className="bp3-input bp3-fill"
              placeholder="e.g. Rooftop unit replacement – Building B"
              autoComplete="off"
              aria-required={true}
              aria-invalid={errors.name ? true : undefined}
              aria-describedby={errors.name ? `${FIELD_IDS.name}-error` : undefined}
              value={object.name || ""}
              onChange={event => this.setField("name", event.target.value)}
            />
            <FieldError id={`${FIELD_IDS.name}-error`}>{errors.name}</FieldError>
          </FormGroup>
        </FormSection>

        <FormSection title="Schedule" hint="When the work starts and when it should wrap up.">
          <div className="row">
            <div className="col-6">
              <FormGroup label="Starts" labelInfo={<span className="fb-required" aria-hidden="true">*</span>}>
                <DateTimeInput
                  placeholder={'Start date'}
                  defaultValue={object.start_date}
                  onChange={this.handleStartDateChange.bind(this)}
                />
              </FormGroup>
            </div>
            <div className="col-6">
              <FormGroup label="Ends" labelInfo={<span className="fb-required" aria-hidden="true">*</span>}>
                <DateTimeInput
                  placeholder={'End date'}
                  defaultValue={object.end_date}
                  onChange={this.handleEndDateChange.bind(this)}
                />
              </FormGroup>
            </div>
          </div>
        </FormSection>

        <FormSection title="Details" hint={estimateClient ? "Projects created from an estimate start as Active." : "Where the project stands, plus the PO and price if you have them."}>
          <div id={FIELD_IDS.status}>
            <Choice
              label="Status"
              name="Status"
              required={!estimateClient}
              value={status}
              disabled={Boolean(estimateClient)}
              onChange={(value) => this.setField("status", value)}
              options={STATUSES}
              error={errors.status}
            />
          </div>

          <MoreOptions label={isLasercontrolsTenant ? "PO number, price and type" : "PO number and price"} open={hasMore}>
            <div className="row">
              <div className="col-6">
                <FormGroup label="PO number" labelFor="project-po-number">
                  <input
                    id="project-po-number"
                    type="text"
                    className="bp3-input bp3-fill"
                    placeholder="e.g. PO-10482"
                    autoComplete="off"
                    value={object.po_number || ""}
                    onChange={event => this.setField("po_number", event.target.value)}
                  />
                </FormGroup>
              </div>
              <div className="col-6">
                <FormGroup label="Price" labelFor="project-price">
                  <input
                    id="project-price"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    className="bp3-input bp3-fill"
                    placeholder="0.00"
                    value={object.price === null || object.price === undefined ? "" : object.price}
                    onChange={event => this.setField("price", event.target.value)}
                  />
                </FormGroup>
              </div>
            </div>

            {isLasercontrolsTenant && (
              <Choice
                label="Project type"
                name="Project type"
                value={object.project_type || null}
                onChange={(value) => this.setField("project_type", value || null)}
                options={PROJECT_TYPES}
              />
            )}
          </MoreOptions>
        </FormSection>
      </FormDrawer>
    );
  }
}

export default AddProjectModal;
